import * as React from 'react';

// =============================================================================
// analysis-column-widths.ts — Largeurs de colonnes du ranking, MÉMORISÉES entre visites (U1-bis).
// =============================================================================
// RETOUR GATE_HUMAN du 2026-09-18 (verbatim) : « La largeur des colonnes doit être modificable par
// l'utilisateur. » Réponse à la question 2 : « 2. OUi » ⇒ les largeurs sont MÉMORISÉES ENTRE VISITES.
//
// DÉCISION GATE_0 S14 (G0-S14-5) — VERBATIM : « Une préférence d'AFFICHAGE ≠ donnée métier. La règle
// "zéro DONNÉE MÉTIER en localStorage" N'EST PAS violée (seules des préférences d'affichage y vont. »
// Une largeur de colonne est une préférence d'affichage PURE : elle ne porte ni TJM, ni SIRET, ni rang,
// ni filtre. RULE_REF_006 (no-restricted-properties sur `localStorage`) cible les DONNÉES MÉTIER :
// l'accès stockage est ISOLÉ ICI, dans un module dédié, avec un `eslint-disable` JUSTIFIÉ — même patron
// que `routes/admin/operational-view/operational-view-preferences.ts` (précédent du repo).
//
// CE QUI N'EST PAS PERSISTÉ : les filtres, le TOP N, la chip de tag et le tri — ils vivent dans l'URL
// (RULE_REF_006, vue partageable), et la sélection en masse reste in-memory.
//
// PARSE FAIL-SAFE : clé absente, JSON invalide, valeur non numérique, NaN/Infinity, largeur hors bornes
// ou id de colonne inconnu ⇒ la valeur est IGNORÉE, jamais un throw et jamais un vidage brutal. La page
// MONTE toujours, avec les largeurs par défaut du tableau.

/** Clé VERSIONNÉE dédiée à la page Analyse fusionnée (aucun partage avec la vue opérationnelle). */
const STORAGE_KEY = 'jeko.analysis.ranking-column-widths.v1';

/** Largeur minimale d'une colonne (px) : en dessous, l'en-tête n'est plus lisible. */
export const MIN_COLUMN_WIDTH = 36;
/** Largeur maximale d'une colonne (px) : au-dessus, le tableau devient inexploitable. */
export const MAX_COLUMN_WIDTH = 520;

/** Largeurs persistées, par id de colonne (px). Une colonne absente garde sa largeur par défaut. */
export type ColumnWidths = Readonly<Record<string, number>>;

/** Borne une largeur dans [MIN, MAX] ; `null` si la valeur n'est pas un nombre exploitable. */
export function clampColumnWidth(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(value)));
}

/**
 * Parse défensif d'une valeur inconnue en largeurs de colonnes : seules les entrées dont l'id est
 * CONNU de la page et dont la largeur est un nombre fini sont retenues (et bornées). Tout le reste est
 * silencieusement écarté.
 */
export function sanitizeColumnWidths(
  raw: unknown,
  knownColumnIds: readonly string[],
): ColumnWidths {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return {};
  }
  const known = new Set(knownColumnIds);
  const result: Record<string, number> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!known.has(id)) {
      continue;
    }
    const width = clampColumnWidth(value);
    if (width !== null) {
      result[id] = width;
    }
  }
  return result;
}

/**
 * Lit les largeurs mémorisées (fail-safe). Storage indisponible, clé absente ou contenu corrompu ⇒
 * `{}` (largeurs par défaut du tableau). NE jette JAMAIS.
 */
export function loadColumnWidths(knownColumnIds: readonly string[]): ColumnWidths {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    // G0-S14-5 : préférence d'AFFICHAGE (largeur de colonne), PAS une donnée métier. Accès ISOLÉ.
    // eslint-disable-next-line no-restricted-properties
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? {} : sanitizeColumnWidths(JSON.parse(raw), knownColumnIds);
  } catch {
    // JSON invalide / storage indisponible (mode privé strict) : on dégrade sur les largeurs par défaut.
    return {};
  }
}

/** Écrit les largeurs mémorisées (no-op si le storage est indisponible). */
export function saveColumnWidths(widths: ColumnWidths): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    // G0-S14-5 : préférence d'AFFICHAGE seulement, clé versionnée dédiée à cette page.
    // eslint-disable-next-line no-restricted-properties
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Quota / mode privé strict : la préférence dégrade en in-memory pour la session courante.
    // Aucune remontée d'erreur : une largeur de colonne non mémorisée n'est pas un échec fonctionnel.
  }
}

/**
 * Hook : largeurs de colonnes du ranking, chargées UNE FOIS au montage et réécrites à chaque
 * ajustement. La source de vérité est l'état React in-memory ; le stockage n'en est qu'un MIROIR.
 */
export function useRankingColumnWidths(
  knownColumnIds: readonly string[],
): readonly [ColumnWidths, (columnId: string, width: number) => void, () => void] {
  // `knownColumnIds` est une constante de module côté appelant : la lecture initiale n'est faite
  // qu'au montage (initialiseur paresseux), jamais à chaque rendu.
  const [widths, setWidths] = React.useState<ColumnWidths>(() =>
    loadColumnWidths(knownColumnIds),
  );

  const setColumnWidth = React.useCallback((columnId: string, width: number): void => {
    const clamped = clampColumnWidth(width);
    if (clamped === null) {
      return;
    }
    setWidths((prev) => {
      if (prev[columnId] === clamped) {
        return prev;
      }
      const next = { ...prev, [columnId]: clamped };
      saveColumnWidths(next);
      return next;
    });
  }, []);

  const resetColumnWidths = React.useCallback((): void => {
    setWidths({});
    saveColumnWidths({});
  }, []);

  return [widths, setColumnWidth, resetColumnWidths] as const;
}
